import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Download, LogIn, LogOut, Menu, X, ChevronDown, User, UserPlus, CreditCard, Bell, Shield, Globe, Sun, Moon } from 'lucide-react';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import StonewallLogo from './StonewallLogo';
import { isAuthenticated, logout } from '../services/auth';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const { currentUser: user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close user dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };


  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleLogout = async () => {
    await logout();
    refreshUser();
    navigate('/');
  };

  const getActiveTab = (path) => {
    return location.pathname === path;
  };

  // Navigation links
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Features', path: '/features' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="bg-gray-300 dark:bg-gray-800 shadow-md border-b border-gray-400 dark:border-gray-700 w-full">
      <div className="px-0">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center pl-3 pr-1">
                <StonewallLogo className="h-10 w-10" />
              </Link>
            </div>
            <div className="hidden sm:ml-2 sm:flex sm:items-stretch sm:border-l sm:border-gray-400 dark:sm:border-gray-600 sm:divide-x sm:divide-gray-400 dark:sm:divide-gray-600">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                    getActiveTab(link.path)
                      ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-400/20 dark:hover:bg-gray-700/30'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-stretch sm:divide-x sm:divide-gray-400 dark:sm:divide-gray-600">
            <Link
              to="/downloads"
              className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                getActiveTab('/downloads')
                  ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-400/20 dark:hover:bg-gray-700/30'
              }`}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Link>

            {user && (
              // Subscription button - standalone
              <Link
                to="/account/subscription"
                className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                  location.pathname === '/account/subscription'
                    ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-400/20 dark:hover:bg-gray-700/30'
                }`}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Subscription
              </Link>
            )}

            {user ? (
              // User dropdown with Settings and Sign out
              <div className="relative flex items-stretch" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  aria-controls="user-menu"
                  className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                    isUserMenuOpen
                      ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-400/20 dark:hover:bg-gray-700/30'
                  }`}
                >
                  <User className="w-4 h-4 mr-2" />
                  {user.email}
                  <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isUserMenuOpen && (
                  <div id="user-menu" className="absolute right-0 top-full w-52 bg-gray-300 dark:bg-gray-800 shadow-lg z-10 border border-gray-400 dark:border-gray-700 rounded-b-md overflow-hidden divide-y divide-gray-400 dark:divide-gray-700">
                      <Link
                        to="/account/settings?tab=profile"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" />
                        Profile
                      </Link>

                      <Link
                        to="/account/settings?tab=general"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Globe className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" />
                        General
                      </Link>

                      <Link
                        to="/account/settings?tab=notifications"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Bell className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" />
                        Notifications
                      </Link>

                      <Link
                        to="/account/settings?tab=security"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Shield className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" />
                        Security
                      </Link>

                      <Link
                        to="/account/settings?tab=payment"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <CreditCard className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" />
                        Payment
                      </Link>

                    {user.role === 'Admin' && (
                      <Link
                        to="/admin/dashboard"
                        className="flex items-center px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-300 hover:bg-gray-400/40 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-600 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        Admin Panel
                      </Link>
                    )}

                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center px-4 py-2.5 text-sm font-medium text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign out
                      </button>
                  </div>
                )}
              </div>
            ) : (
              // Login/Register buttons for logged out users
              <>
                <Link to="/login" className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                  getActiveTab('/login')
                    ? 'text-blue-700 dark:text-blue-300 bg-blue-100/50 dark:bg-blue-900/30'
                    : 'text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in
                </Link>
                <Link to="/register" className={`inline-flex items-center px-5 text-sm font-medium transition-colors ${
                  getActiveTab('/register')
                    ? 'text-gray-900 dark:text-white bg-gray-400/40 dark:bg-gray-700/50'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-400/20 dark:hover:bg-gray-700/30'
                }`}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register
                </Link>
              </>
            )}

            <button
              onClick={toggleDarkMode}
              className="inline-flex items-center justify-center px-3 text-gray-600 dark:text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-300 hover:bg-gray-400/20 dark:hover:bg-gray-700/30 transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
            >
              {isMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden`}>
        <div className="pt-2 pb-3 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium ${
                getActiveTab(link.path)
                  ? 'border-blue-500 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900'
                  : 'border-transparent text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <div className="mt-4 flex flex-col space-y-2 px-3">
            {user ? (
              // Mobile user menu with account links
              <>
                <div className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-md">
                  Signed in as <strong>{user.email}</strong>
                </div>

                <Link
                  to="/account/subscription"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription
                </Link>

                <Link
                  to="/account/settings?tab=profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Link>

                <Link
                  to="/account/settings?tab=general"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  General
                </Link>

                <Link
                  to="/account/settings?tab=notifications"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                </Link>

                <Link
                  to="/account/settings?tab=security"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>

                <Link
                  to="/account/settings?tab=payment"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Payment
                </Link>

                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </button>
              </>
            ) : (
              // Mobile login/register
              <>
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign in
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Register
                </Link>
              </>
            )}
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <Link
                to="/downloads"
                onClick={() => setIsMenuOpen(false)}
                className="block bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Link>
              <button
                onClick={toggleDarkMode}
                className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center transition-colors"
              >
                {darkMode ? <Sun className="w-4 h-4 mr-2 text-yellow-400" /> : <Moon className="w-4 h-4 mr-2 text-gray-500" />}
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;